import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

interface PaymentRow {
  id: string;
  created_at: string;
  paid_at: string | null;
  status: string;
  mode: string;
  amount: number;
  currency: string;
  member: { name: string | null; email: string | null; chapter: string | null } | null;
  application: { first_name: string | null; last_name: string | null; email: string | null; chapter: string | null } | null;
  stripe_checkout_session_id: string | null;
  stripe_payment_intent_id: string | null;
  stripe_subscription_id: string | null;
}

const statusBadge: Record<string, string> = {
  pending: "bg-blue-500/15 text-blue-700 dark:text-blue-300",
  completed: "bg-green-500/15 text-green-700 dark:text-green-300",
  failed: "bg-red-500/15 text-red-700 dark:text-red-300",
  refunded: "bg-slate-500/15 text-slate-700 dark:text-slate-300",
  superseded: "bg-muted text-muted-foreground",
};

const AdminPayments = () => {
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showSuperseded, setShowSuperseded] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("payments")
      .select(
        "id, created_at, paid_at, status, mode, amount, currency, stripe_checkout_session_id, stripe_payment_intent_id, stripe_subscription_id, member:member_id (name, email, chapter), application:application_id (first_name, last_name, email, chapter)"
      )
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) {
      toast.error(error.message);
    } else {
      setRows((data as unknown as PaymentRow[]) || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const base = showSuperseded ? rows : rows.filter((r) => r.status !== "superseded");
    if (!search.trim()) return base;
    const q = search.toLowerCase();
    return base.filter((r) => {
      const m = r.member;
      const a = r.application;
      const name = m?.name || `${a?.first_name || ""} ${a?.last_name || ""}`.trim() || "";
      const email = m?.email || a?.email || "";
      return (
        name.toLowerCase().includes(q) ||
        email.toLowerCase().includes(q) ||
        (r.stripe_checkout_session_id || "").toLowerCase().includes(q) ||
        (r.stripe_payment_intent_id || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, showSuperseded]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <CardTitle>Payments</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Stripe checkout and payment history for memberships and renewals.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap">
              <input
                type="checkbox"
                checked={showSuperseded}
                onChange={(e) => setShowSuperseded(e.target.checked)}
              />
              Show superseded
            </label>
            <Input
              placeholder="Search by name, email, or Stripe ID"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-64"
            />
            <Button variant="outline" size="icon" onClick={load} disabled={loading}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              {search ? "No payments match your search." : "No payments recorded yet."}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Member / Applicant</TableHead>
                    <TableHead>Chapter</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Stripe session</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => {
                    const name =
                      r.member?.name ||
                      `${r.application?.first_name || ""} ${r.application?.last_name || ""}`.trim() ||
                      "—";
                    const email = r.member?.email || r.application?.email || "—";
                    const chapter = r.member?.chapter || r.application?.chapter || "—";
                    return (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap">
                          {r.paid_at
                            ? new Date(r.paid_at).toLocaleDateString("en-US")
                            : new Date(r.created_at).toLocaleDateString("en-US")}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{name}</div>
                          <div className="text-xs text-muted-foreground">{email}</div>
                        </TableCell>
                        <TableCell className="capitalize">{chapter}</TableCell>
                        <TableCell className="capitalize">
                          {r.mode === "new_member" ? "New member" : "Renewal"}
                        </TableCell>
                        <TableCell>
                          ${(r.amount / 100).toFixed(2)} {r.currency.toUpperCase()}
                        </TableCell>
                        <TableCell>
                          <Badge className={statusBadge[r.status] || ""} variant="secondary">
                            {r.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs max-w-[160px] truncate">
                          {r.stripe_checkout_session_id || "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPayments;
